import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./lib/api.js";
import { isUpcoming, sortEvents, tidyUrl } from "./lib/format.js";
import { applyTheme, readTheme } from "./lib/theme.js";
import Header, { NAV } from "./components/Header.jsx";
import ThemeToggle from "./components/ThemeToggle.jsx";
import Hero from "./components/Hero.jsx";
import Benefits from "./components/Benefits.jsx";
import Events from "./components/Events.jsx";
import Members from "./components/Members.jsx";
import Join from "./components/Join.jsx";
import LinkedInBanner from "./components/LinkedInBanner.jsx";
import Footer from "./components/Footer.jsx";
import Logo from "./components/Logo.jsx";
import Login from "./admin/Login.jsx";
import Admin from "./admin/Admin.jsx";

const ADMIN_PATH = "/verwaltung";
const GA_ID = "G-J5N9RNT8HT";
const SITE_URL = "https://unternehmer-rj.ch/";

/** schema.org-Einträge der kommenden Anlässe; Zeiten in Schweizer Ortszeit. */
function eventsJsonLd(site) {
  const offset = (iso) => {
    const d = new Date(`${iso}T12:00:00`);
    const m = -d.getTimezoneOffset();
    const pad = (n) => String(Math.abs(n)).padStart(2, "0");
    return `${m >= 0 ? "+" : "-"}${pad(Math.trunc(m / 60))}:${pad(m % 60)}`;
  };
  const at = (date, time) => (time ? `${date}T${time}:00${offset(date)}` : date);
  return {
    "@context": "https://schema.org",
    "@graph": (site.events || [])
      .filter((e) => isUpcoming(e.date))
      .map((e) => ({
        "@type": "Event",
        name: e.title,
        description: e.description || `Anlass der ${site.settings.groupName}`,
        startDate: at(e.date, e.timeFrom),
        ...(e.timeTo ? { endDate: at(e.date, e.timeTo) } : {}),
        eventStatus: "https://schema.org/EventScheduled",
        eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
        location: {
          "@type": "Place",
          name: e.location || site.settings.region,
          address: {
            "@type": "PostalAddress",
            addressLocality: e.location?.split(",").pop().trim() || site.settings.region,
            addressCountry: "CH",
          },
        },
        organizer: { "@type": "Organization", name: site.settings.groupName, url: SITE_URL },
        url: e.link ? tidyUrl(e.link) : `${SITE_URL}#anlaesse`,
        image: `${SITE_URL}og-image.jpg`,
      })),
  };
}

/** Zwei Ansichten, echte Adressen – ohne zusätzliche Router-Bibliothek. */
function usePath() {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const go = useCallback((next) => {
    if (window.location.pathname !== next) {
      window.history.pushState({}, "", next);
      setPath(next);
      window.scrollTo(0, 0);
    }
  }, []);
  return [path, go];
}

/** Helle oder dunkle Ansicht, gemerkt über Besuche hinweg. Hell ist Standard. */
function useTheme() {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);
  return [theme, toggle];
}

function useToast() {
  const [toast, setToast] = useState(null);
  const timer = useRef(0);
  const notify = useCallback((message, kind = "ok") => {
    setToast({ message, kind });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), kind === "error" ? 6000 : 3000);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return [toast, notify];
}

export default function App() {
  const [path, go] = usePath();
  const [site, setSite] = useState(null);
  const [siteError, setSiteError] = useState("");
  const [admin, setAdmin] = useState(null); // Daten der Verwaltung, wenn angemeldet
  const [checkedSession, setCheckedSession] = useState(false);
  const [active, setActive] = useState("");
  const [theme, toggleTheme] = useTheme();
  const [toast, notify] = useToast();

  const onAdminPath = path.startsWith(ADMIN_PATH);

  /* --- Öffentliche Daten laden --- */
  useEffect(() => {
    let alive = true;
    api
      .site()
      .then((d) => alive && setSite(d))
      .catch((ex) => alive && setSiteError(ex.message));
    return () => {
      alive = false;
    };
  }, []);

  /* --- Bestehende Sitzung der Verwaltung erkennen --- */
  useEffect(() => {
    if (!onAdminPath || admin || checkedSession) return;
    let alive = true;
    api
      .session()
      .then(({ signedIn }) => (signedIn ? api.adminData() : null))
      .then((d) => alive && d && setAdmin(d))
      .catch(() => {})
      .finally(() => alive && setCheckedSession(true));
    return () => {
      alive = false;
    };
  }, [onAdminPath, admin, checkedSession]);

  /* --- Aktiver Menüpunkt beim Scrollen --- */
  useEffect(() => {
    if (onAdminPath || !site) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (vis) setActive(vis.target.id);
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: [0, 0.25, 0.5] }
    );
    NAV.forEach((n) => {
      const el = document.getElementById(n.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [onAdminPath, site]);

  /* --- Titel der Seite, Suchmaschinen und Statistik --- */
  useEffect(() => {
    const name = site?.settings?.groupName || "Jung Unternehmer Rapperswil-Jona";
    document.title = onAdminPath
      ? `Verwaltung · ${name}`
      : `${name} – Netzwerk für Startups & Unternehmer`;
    // Die Verwaltung gehört weder in den Index noch in Google Analytics.
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = onAdminPath ? "noindex, nofollow" : "index, follow, max-image-preview:large";
    window[`ga-disable-${GA_ID}`] = onAdminPath;
  }, [onAdminPath, site]);

  /* --- Kommende Anlässe als strukturierte Daten für Google --- */
  useEffect(() => {
    if (onAdminPath || !site) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(eventsJsonLd(site));
    document.head.appendChild(el);
    return () => el.remove();
  }, [onAdminPath, site]);

  /** Nach Änderungen in der Verwaltung auch die öffentliche Ansicht auffrischen. */
  const applyAdminData = useCallback((next) => {
    setAdmin(next);
    setSite({
      settings: next.settings,
      benefits: next.benefits,
      events: next.events,
      members: next.members,
    });
  }, []);

  const leaveAdmin = useCallback(
    (sessionLost) => {
      if (sessionLost) {
        setAdmin(null);
        setCheckedSession(true);
        notify("Die Sitzung ist abgelaufen. Bitte nochmals anmelden.", "error");
        return;
      }
      go("/");
    },
    [go, notify]
  );

  const stats = useMemo(() => {
    const members = site?.members || [];
    return {
      members: members.length,
      branches: new Set(members.map((m) => m.branch).filter(Boolean)).size,
    };
  }, [site]);

  const nextEvent = useMemo(() => {
    const events = site?.events || [];
    return events.filter((e) => isUpcoming(e.date)).sort(sortEvents.upcoming)[0] || null;
  }, [site]);

  /* ---------------- Verwaltung ---------------- */

  if (onAdminPath) {
    return (
      <div className="app">
        <header className="admin-head">
          <div className="head-in">
            <a className="brand" href="/" onClick={(e) => { e.preventDefault(); go("/"); }}>
              <Logo className="brand-mark" />
              <span className="brand-text">
                Verwaltung
                <small>{site?.settings?.short || "JU Rapperswil-Jona"}</small>
              </span>
            </a>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
              <ThemeToggle theme={theme} onToggle={toggleTheme} />
              {admin ? (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={async () => {
                    try {
                      await api.signOut();
                    } catch {
                      /* egal, lokal trotzdem abmelden */
                    }
                    setAdmin(null);
                    go("/");
                  }}
                >
                  Abmelden
                </button>
              ) : null}
              <button className="btn btn-ghost btn-sm" onClick={() => go("/")}>
                Zur Seite
              </button>
            </div>
          </div>
        </header>

        {!checkedSession ? (
          <p className="loading">Wird geladen ...</p>
        ) : admin ? (
          <Admin data={admin} setData={applyAdminData} notify={notify} onExit={leaveAdmin} />
        ) : (
          <Login
            onSignedIn={(d) => {
              applyAdminData(d);
              notify("Willkommen in der Verwaltung.");
            }}
            onExit={() => go("/")}
          />
        )}

        {toast ? (
          <div className="toast" data-kind={toast.kind} role="status">
            {toast.message}
          </div>
        ) : null}
      </div>
    );
  }

  /* ---------------- Öffentliche Seite ---------------- */

  if (siteError) {
    return (
      <div className="app">
        <div className="loading">
          <Logo style={{ height: 48, color: "var(--accent)", display: "block", marginBottom: 20 }} />
          <p>Die Seite lässt sich gerade nicht laden. {siteError}</p>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div className="app">
        <div className="loading">
          <Logo style={{ height: 48, color: "var(--accent)", display: "block", marginBottom: 20 }} />
          <p>Wird geladen ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <a className="skip-link" href="#anlaesse">
        Zum Inhalt springen
      </a>

      <Header
        settings={site.settings}
        active={active}
        theme={theme}
        onToggleTheme={toggleTheme}
        onAdmin={() => go(ADMIN_PATH)}
      />

      <main>
        <Hero settings={site.settings} next={nextEvent} stats={stats} />
        <Benefits benefits={site.benefits} />
        <Events events={site.events} showPastEnabled={site.settings.showPastEvents} />
        <Members members={site.members} />
        <Join settings={site.settings} />
        <LinkedInBanner settings={site.settings} />
      </main>

      <Footer settings={site.settings} stats={stats} onAdmin={() => go(ADMIN_PATH)} />
    </div>
  );
}
